import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Download, Check, X, CalendarClock, Users, Filter, Trash2, Plus, StickyNote, ChevronLeft, ChevronRight, Globe } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { errMsg, API } from "@/lib/api";
import { CATEGORY_KEYS, LANGS } from "@/i18n";
import { TIME_SLOTS } from "@/data";
import SettingsPanel from "@/components/admin/SettingsPanel";
import MessagesPanel from "@/components/admin/MessagesPanel";
import ContentPanel from "@/components/admin/ContentPanel";
import ProductsPanel from "@/components/admin/ProductsPanel";
import BlogPanel from "@/components/admin/BlogPanel";
import PagesPanel from "@/components/admin/PagesPanel";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const STATUS_STYLE = {
  pending: "bg-[rgba(234,179,8,0.15)] text-yellow-300 border-yellow-500/30",
  approved: "bg-[rgba(34,197,94,0.15)] text-green-300 border-green-500/30",
  rejected: "bg-[rgba(239,68,68,0.15)] text-red-300 border-red-500/30",
  cancelled: "bg-[rgba(239,68,68,0.12)] text-red-300/80 border-red-500/20",
  rescheduled: "bg-[rgba(59,130,246,0.15)] text-blue-300 border-blue-500/30",
};

const DOT = {
  pending: "bg-yellow-400",
  approved: "bg-green-400",
  rejected: "bg-red-400",
  cancelled: "bg-red-400/50",
  rescheduled: "bg-blue-400",
};

function CalendarView({ appts, t, lang }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selected, setSelected] = useState(null);
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const monthLabel = cursor.toLocaleString(lang === "tr" ? "tr-TR" : lang === "en" ? "en-GB" : "de-DE", { month: "long", year: "numeric" });
  const startOffset = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const byDate = {};
  appts.forEach((a) => { (byDate[a.date] = byDate[a.date] || []).push(a); });
  const todayStr = new Date().toISOString().slice(0, 10);

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const ds = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ d, ds, items: (byDate[ds] || []).sort((a, b) => a.time.localeCompare(b.time)) });
  }
  const weekdays = { de: ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"], en: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"], tr: ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"] }[lang] || ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
  const move = (n) => { setCursor(new Date(year, month + n, 1)); setSelected(null); };

  return (
    <div data-testid="calendar-view">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-serif-display text-2xl text-[var(--vl-ivory)] capitalize">{monthLabel}</h3>
        <div className="flex gap-2">
          <button onClick={() => move(-1)} className="vl-btn-outline rounded-full w-9 h-9 flex items-center justify-center" data-testid="cal-prev"><ChevronLeft size={16} /></button>
          <button onClick={() => setCursor(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); })} className="vl-btn-outline rounded-full px-4 text-xs tracking-widest uppercase" data-testid="cal-today">•</button>
          <button onClick={() => move(1)} className="vl-btn-outline rounded-full w-9 h-9 flex items-center justify-center" data-testid="cal-next"><ChevronRight size={16} /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1.5 mb-1.5">
        {weekdays.map((w) => <div key={w} className="text-center text-[10px] tracking-widest uppercase text-[var(--vl-stone)] py-1">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((c, i) => (
          <div key={i} className={`min-h-[86px] rounded-lg p-1.5 border ${c ? "bg-[var(--vl-obsidian)] border-[var(--vl-border)]" : "border-transparent"} ${c && c.ds === todayStr ? "ring-1 ring-[var(--vl-gold)]" : ""}`}>
            {c && (
              <button onClick={() => setSelected(c.items.length ? c : null)} className="w-full text-left" data-testid={`cal-day-${c.ds}`}>
                <div className={`text-xs mb-1 ${c.ds === todayStr ? "text-[var(--vl-gold)] font-semibold" : "text-[var(--vl-stone)]"}`}>{c.d}</div>
                <div className="space-y-0.5">
                  {c.items.slice(0, 3).map((a) => (
                    <div key={a.ref} className="flex items-center gap-1 text-[10px] text-[var(--vl-ivory)] truncate">
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${DOT[a.status] || "bg-stone-400"}`} />
                      <span className="truncate">{a.time} {a.name}</span>
                    </div>
                  ))}
                  {c.items.length > 3 && <div className="text-[10px] text-[var(--vl-gold)]">+{c.items.length - 3}</div>}
                </div>
              </button>
            )}
          </div>
        ))}
      </div>
      {selected && (
        <div className="vl-card rounded-xl p-5 mt-5" data-testid="cal-day-detail">
          <div className="text-sm text-[var(--vl-gold)] tracking-widest uppercase mb-3">{selected.ds}</div>
          <div className="space-y-2">
            {selected.items.map((a) => (
              <div key={a.ref} className="flex items-center justify-between border-b border-[var(--vl-border)] py-2">
                <div className="text-sm text-[var(--vl-ivory)]"><span className="text-[var(--vl-gold)] font-mono-accent">{a.time}</span> · {a.name} · {t(`cat_${a.category}`)}</div>
                <span className={`px-2.5 py-0.5 rounded-full text-xs border ${STATUS_STYLE[a.status] || ""}`}>{t(`status_${a.status}`)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { t, lang, setLang } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("appointments");
  const [stats, setStats] = useState(null);
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fStatus, setFStatus] = useState("");
  const [fCategory, setFCategory] = useState("");
  const [modal, setModal] = useState(null); // {type, appt}
  const [message, setMessage] = useState("");
  const [rDate, setRDate] = useState("");
  const [rTime, setRTime] = useState("");
  const [notesText, setNotesText] = useState("");
  const [staff, setStaff] = useState([]);
  const [newStaff, setNewStaff] = useState({ name: "", email: "", password: "", role: "stylist" });

  const isSuper = user && user.role === "super_admin";

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  const loadAppts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fStatus) params.status = fStatus;
      if (fCategory) params.category = fCategory;
      const [a, s] = await Promise.all([
        api.get("/admin/appointments", { params }),
        api.get("/admin/stats"),
      ]);
      setAppts(a.data);
      setStats(s.data);
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
      else toast.error(errMsg(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [fStatus, fCategory, navigate]);

  const loadStaff = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/staff");
      setStaff(data);
    } catch (e) { /* non-super will 403 */ }
  }, []);

  useEffect(() => { if (user && user.role) loadAppts(); }, [user, loadAppts]);
  useEffect(() => { if (isSuper && tab === "staff") loadStaff(); }, [isSuper, tab, loadStaff]);

  const approve = async (ref) => {
    try { await api.post(`/admin/appointments/${ref}/approve`); toast.success(t("approve")); loadAppts(); }
    catch (e) { toast.error(errMsg(e.response?.data?.detail)); }
  };

  const submitModal = async () => {
    const { type, appt } = modal;
    try {
      if (type === "reject") {
        await api.post(`/admin/appointments/${appt.ref}/reject`, { message });
      } else if (type === "reschedule") {
        await api.post(`/admin/appointments/${appt.ref}/reschedule`, { message, date: rDate || null, time: rTime || null });
      } else if (type === "notes") {
        await api.put(`/admin/appointments/${appt.ref}/notes`, { internal_notes: notesText });
      }
      toast.success("OK");
      setModal(null); setMessage(""); setRDate(""); setRTime("");
      loadAppts();
    } catch (e) { toast.error(errMsg(e.response?.data?.detail)); }
  };

  const addStaff = async () => {
    try {
      await api.post("/admin/staff", newStaff);
      toast.success(t("add_staff"));
      setNewStaff({ name: "", email: "", password: "", role: "stylist" });
      loadStaff();
    } catch (e) { toast.error(errMsg(e.response?.data?.detail)); }
  };

  const removeStaff = async (id) => {
    try { await api.delete(`/admin/staff/${id}`); loadStaff(); }
    catch (e) { toast.error(errMsg(e.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/admin/appointments/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url; a.download = "veloura-appointments.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e.response?.data?.detail)); }
  };

  if (!user || !user.role) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[var(--vl-gold)]" /></div>;
  }

  const statCards = stats ? [
    { label: t("total"), value: stats.total, icon: Users },
    { label: t("pending"), value: stats.pending, icon: CalendarClock },
    { label: t("approved"), value: stats.approved, icon: Check },
    { label: t("upcoming"), value: stats.upcoming, icon: CalendarClock },
  ] : [];

  return (
    <div className="relative min-h-screen">
      <div className="vl-noise" />
      {/* top bar */}
      <header className="vl-glass sticky top-0 z-40 border-b border-[var(--vl-border)]">
        <div className="max-w-7xl mx-auto px-6 h-18 py-4 flex items-center justify-between">
          <div>
            <img src="/veloura-logo.png" alt="Veloura" className="vl-logo h-9 w-auto" />
            <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--vl-stone)] mt-1.5">Admin · {user.name} · {t(user.role)}</div>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-1 text-[var(--vl-stone)]" data-testid="admin-lang-switcher">
              <Globe size={15} className="text-[var(--vl-gold)]" />
              {LANGS.map((l) => (
                <button key={l.code} onClick={() => setLang(l.code)}
                  className={`px-1.5 text-xs tracking-wider uppercase transition-colors ${lang === l.code ? "text-[var(--vl-gold)]" : "hover:text-[var(--vl-ivory)]"}`} data-testid={`admin-lang-${l.code}`}>
                  {l.code}
                </button>
              ))}
            </div>
            <button onClick={() => { logout(); navigate("/admin/login"); }} className="vl-btn-outline rounded-full px-5 py-2.5 text-sm flex items-center gap-2" data-testid="logout-btn">
              <LogOut size={15} /> {t("logout")}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10 relative z-10">
        {/* stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="stats-row">
          {statCards.map((c) => (
            <div key={c.label} className="vl-card rounded-xl p-5">
              <c.icon size={20} className="text-[var(--vl-gold)] mb-3" />
              <div className="font-serif-display text-3xl text-[var(--vl-ivory)]">{c.value}</div>
              <div className="text-xs tracking-widest uppercase text-[var(--vl-stone)] mt-1">{c.label}</div>
            </div>
          ))}
        </div>

        {/* tabs */}
        <div className="flex items-center gap-6 border-b border-[var(--vl-border)] mb-6">
          <button onClick={() => setTab("appointments")} className={`pb-3 text-sm tracking-widest uppercase transition-colors ${tab === "appointments" ? "text-[var(--vl-gold)] border-b-2 border-[var(--vl-gold)]" : "text-[var(--vl-stone)]"}`} data-testid="tab-appointments">{t("appointments")}</button>
          <button onClick={() => setTab("calendar")} className={`pb-3 text-sm tracking-widest uppercase transition-colors ${tab === "calendar" ? "text-[var(--vl-gold)] border-b-2 border-[var(--vl-gold)]" : "text-[var(--vl-stone)]"}`} data-testid="tab-calendar">{t("calendar")}</button>
          {isSuper && <button onClick={() => setTab("staff")} className={`pb-3 text-sm tracking-widest uppercase transition-colors ${tab === "staff" ? "text-[var(--vl-gold)] border-b-2 border-[var(--vl-gold)]" : "text-[var(--vl-stone)]"}`} data-testid="tab-staff">{t("staff")}</button>}
          <button onClick={() => setTab("messages")} className={`pb-3 text-sm tracking-widest uppercase transition-colors ${tab === "messages" ? "text-[var(--vl-gold)] border-b-2 border-[var(--vl-gold)]" : "text-[var(--vl-stone)]"}`} data-testid="tab-messages">{t("messages_tab")}</button>
          {isSuper && <button onClick={() => setTab("settings")} className={`pb-3 text-sm tracking-widest uppercase transition-colors ${tab === "settings" ? "text-[var(--vl-gold)] border-b-2 border-[var(--vl-gold)]" : "text-[var(--vl-stone)]"}`} data-testid="tab-settings">{t("settings_tab")}</button>}
          {isSuper && <button onClick={() => setTab("content")} className={`pb-3 text-sm tracking-widest uppercase transition-colors ${tab === "content" ? "text-[var(--vl-gold)] border-b-2 border-[var(--vl-gold)]" : "text-[var(--vl-stone)]"}`} data-testid="tab-content">{t("content_tab")}</button>}
          {isSuper && <button onClick={() => setTab("pages")} className={`pb-3 text-sm tracking-widest uppercase transition-colors ${tab === "pages" ? "text-[var(--vl-gold)] border-b-2 border-[var(--vl-gold)]" : "text-[var(--vl-stone)]"}`} data-testid="tab-pages">{t("pages_tab")}</button>}
          {isSuper && <button onClick={() => setTab("products")} className={`pb-3 text-sm tracking-widest uppercase transition-colors ${tab === "products" ? "text-[var(--vl-gold)] border-b-2 border-[var(--vl-gold)]" : "text-[var(--vl-stone)]"}`} data-testid="tab-products">{t("products_tab")}</button>}
          {isSuper && <button onClick={() => setTab("blog")} className={`pb-3 text-sm tracking-widest uppercase transition-colors ${tab === "blog" ? "text-[var(--vl-gold)] border-b-2 border-[var(--vl-gold)]" : "text-[var(--vl-stone)]"}`} data-testid="tab-blog">{t("blog_tab")}</button>}
        </div>

        {tab === "appointments" && (
          <>
            {/* filters */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <Filter size={16} className="text-[var(--vl-gold)]" />
              <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="vl-input rounded-lg px-3 py-2 text-sm" data-testid="filter-status">
                <option value="">{t("all")} · {t("filter_status")}</option>
                {["pending", "approved", "rescheduled", "rejected", "cancelled"].map((s) => <option key={s} value={s}>{t(`status_${s}`)}</option>)}
              </select>
              <select value={fCategory} onChange={(e) => setFCategory(e.target.value)} className="vl-input rounded-lg px-3 py-2 text-sm" data-testid="filter-category">
                <option value="">{t("all")} · {t("filter_category")}</option>
                {CATEGORY_KEYS.map((c) => <option key={c} value={c}>{t(`cat_${c}`)}</option>)}
              </select>
              <button onClick={exportCsv} className="vl-btn-outline rounded-lg px-4 py-2 text-sm flex items-center gap-2 ml-auto" data-testid="export-csv-btn">
                <Download size={15} /> {t("export_csv")}
              </button>
            </div>

            {loading ? (
              <div className="py-20 text-center"><Loader2 className="animate-spin text-[var(--vl-gold)] mx-auto" /></div>
            ) : (
              <div className="space-y-3" data-testid="appointments-list">
                {appts.length === 0 && <p className="text-center text-[var(--vl-stone)] py-16">—</p>}
                {appts.map((a) => (
                  <div key={a.ref} className="vl-card rounded-xl p-5" data-testid={`appt-row-${a.ref}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-mono-accent text-[var(--vl-gold)] tracking-wider">{a.ref}</span>
                          <span className={`px-2.5 py-0.5 rounded-full text-xs border ${STATUS_STYLE[a.status] || ""}`}>{t(`status_${a.status}`)}</span>
                          <span className="text-xs text-[var(--vl-stone)] uppercase tracking-wider">{t(`cat_${a.category}`)}</span>
                        </div>
                        <div className="mt-2 text-[var(--vl-ivory)] font-medium">{a.name} · <span className="text-[var(--vl-gold)]">{a.date} {a.time}</span></div>
                        <div className="text-sm text-[var(--vl-stone)] mt-0.5">{a.email} · {a.phone} · {a.guest_count} {t("guest")}</div>
                        {a.notes && <div className="text-sm text-[var(--vl-stone)]/80 italic mt-1">“{a.notes}”</div>}
                        {a.internal_notes && <div className="text-xs text-[var(--vl-gold)]/80 mt-1 flex items-center gap-1"><StickyNote size={12} /> {a.internal_notes}</div>}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {isSuper && a.status !== "approved" && a.status !== "cancelled" && (
                          <button onClick={() => approve(a.ref)} className="rounded-full px-4 py-2 text-xs flex items-center gap-1.5 bg-green-500/15 text-green-300 border border-green-500/30 hover:bg-green-500/25 transition-colors" data-testid={`approve-${a.ref}`}>
                            <Check size={14} /> {t("approve")}
                          </button>
                        )}
                        {isSuper && a.status !== "rejected" && a.status !== "cancelled" && (
                          <>
                            <button onClick={() => { setModal({ type: "reschedule", appt: a }); setRDate(a.date); setRTime(a.time); }} className="vl-btn-outline rounded-full px-4 py-2 text-xs flex items-center gap-1.5" data-testid={`reschedule-${a.ref}`}>
                              <CalendarClock size={14} /> {t("reschedule")}
                            </button>
                            <button onClick={() => setModal({ type: "reject", appt: a })} className="rounded-full px-4 py-2 text-xs flex items-center gap-1.5 border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors" data-testid={`reject-${a.ref}`}>
                              <X size={14} /> {t("reject")}
                            </button>
                          </>
                        )}
                        <button onClick={() => { setModal({ type: "notes", appt: a }); setNotesText(a.internal_notes || ""); }} className="vl-btn-outline rounded-full px-4 py-2 text-xs flex items-center gap-1.5" data-testid={`notes-${a.ref}`}>
                          <StickyNote size={14} /> {t("internal_notes")}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "calendar" && <CalendarView appts={appts} t={t} lang={lang} />}

        {tab === "staff" && isSuper && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="staff-tab">
            <div className="vl-card rounded-xl p-6 space-y-3">
              {staff.map((s) => (
                <div key={s.id} className="flex items-center justify-between border-b border-[var(--vl-border)] py-3">
                  <div>
                    <div className="text-[var(--vl-ivory)]">{s.name}</div>
                    <div className="text-xs text-[var(--vl-stone)]">{s.email} · {t(s.role)}</div>
                  </div>
                  {s.role !== "super_admin" && (
                    <button onClick={() => removeStaff(s.id)} className="text-red-300 hover:text-red-200" data-testid={`remove-staff-${s.id}`}><Trash2 size={16} /></button>
                  )}
                </div>
              ))}
            </div>
            <div className="vl-card rounded-xl p-6 space-y-4">
              <h3 className="font-serif-display text-xl text-[var(--vl-ivory)] flex items-center gap-2"><Plus size={18} className="text-[var(--vl-gold)]" /> {t("add_staff")}</h3>
              <input placeholder={t("full_name")} value={newStaff.name} onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })} className="vl-input rounded-lg px-4 py-2.5 w-full" data-testid="staff-name-input" />
              <input type="email" placeholder={t("email")} value={newStaff.email} onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })} className="vl-input rounded-lg px-4 py-2.5 w-full" data-testid="staff-email-input" />
              <input type="password" placeholder={t("password")} value={newStaff.password} onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })} className="vl-input rounded-lg px-4 py-2.5 w-full" data-testid="staff-password-input" />
              <select value={newStaff.role} onChange={(e) => setNewStaff({ ...newStaff, role: e.target.value })} className="vl-input rounded-lg px-4 py-2.5 w-full" data-testid="staff-role-select">
                <option value="stylist">{t("stylist")}</option>
                <option value="super_admin">{t("super_admin")}</option>
              </select>
              <button onClick={addStaff} className="vl-btn-gold rounded-full px-6 py-2.5 text-sm font-medium w-full" data-testid="add-staff-btn">{t("add_staff")}</button>
            </div>
          </div>
        )}

        {tab === "messages" && <MessagesPanel />}
        {tab === "settings" && isSuper && <SettingsPanel />}
        {tab === "content" && isSuper && <ContentPanel />}
        {tab === "pages" && isSuper && <PagesPanel />}
        {tab === "products" && isSuper && <ProductsPanel />}
        {tab === "blog" && isSuper && <BlogPanel />}
      </div>

      {/* action modal */}
      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="bg-[var(--vl-surface)] border border-[var(--vl-border)] text-[var(--vl-ivory)]">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">
              {modal?.type === "reject" ? t("reject") : modal?.type === "reschedule" ? t("reschedule") : t("internal_notes")}
            </DialogTitle>
          </DialogHeader>
          {modal?.type === "notes" ? (
            <textarea value={notesText} onChange={(e) => setNotesText(e.target.value)} rows={4} className="vl-input rounded-lg px-4 py-3 w-full resize-none" data-testid="modal-notes-input" />
          ) : (
            <div className="space-y-4">
              {modal?.type === "reschedule" && (
                <div className="space-y-3">
                  <input type="date" value={rDate} onChange={(e) => setRDate(e.target.value)} className="vl-input rounded-lg px-4 py-2.5 w-full" data-testid="modal-date-input" />
                  <div className="flex gap-2">
                    {TIME_SLOTS.map((s) => (
                      <button key={s.value} onClick={() => setRTime(s.value)} className={`px-4 py-2 rounded-lg border text-sm ${rTime === s.value ? "border-[var(--vl-gold)] text-[var(--vl-gold)]" : "border-[var(--vl-border)] text-[var(--vl-stone)]"}`}>{s.value}</button>
                    ))}
                  </div>
                </div>
              )}
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder={t("message_optional")} className="vl-input rounded-lg px-4 py-3 w-full resize-none" data-testid="modal-message-input" />
            </div>
          )}
          <DialogFooter>
            <button onClick={submitModal} className="vl-btn-gold rounded-full px-6 py-2.5 text-sm font-medium" data-testid="modal-submit-btn">{t("send")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
